import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { submitLead } from '../api';
import { isValidPhone, PHONE_ERROR } from '../lib/phone';
import { PhoneField } from '../components/PhoneField';
import { trackLeadConversion } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const Contact = () => {
  const { t, settings } = useApp();
  const companyInfo = settings;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', mobile: '', email: '', note: '' });
  useEffect(() => { window.scrollTo(0, 0); document.title = 'Contact | GOBRA Capital Real Estate'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.mobile)) { setError(PHONE_ERROR); return; }
    setError('');
    try {
      await submitLead({ ...form, project: 'Contact Page' });
      trackLeadConversion();
    } catch (err) {
      const leads = JSON.parse(localStorage.getItem('gobra_leads') || '[]');
      leads.push({ ...form, project: 'Contact Page', ts: Date.now() });
      localStorage.setItem('gobra_leads', JSON.stringify(leads));
    }
    setSent(true);
  };

  return (
    <main className="pt-24 md:pt-28">
      <div className="bg-gobra-slate py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="h-1 w-12 bg-gobra-orange rounded-full mb-3" />
          <h1 className="font-title text-3xl md:text-5xl font-bold text-white">{t('get_in_touch')}</h1>
        </div>
      </div>
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-title text-2xl font-bold text-gobra-slate mb-5">{companyInfo.name}</h2>
          <div className="space-y-4">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-gobra-slate hover:text-gobra-green"><span className="h-11 w-11 rounded-full bg-gobra-green/10 flex items-center justify-center"><Phone className="h-5 w-5 text-gobra-green" /></span>{companyInfo.phone}</a>
            <a href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-gobra-slate hover:text-gobra-green"><span className="h-11 w-11 rounded-full bg-gobra-green/10 flex items-center justify-center"><MessageCircle className="h-5 w-5 text-gobra-green" /></span>WhatsApp {companyInfo.whatsapp}</a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-gobra-slate hover:text-gobra-green"><span className="h-11 w-11 rounded-full bg-gobra-green/10 flex items-center justify-center"><Mail className="h-5 w-5 text-gobra-green" /></span>{companyInfo.email}</a>
            <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gobra-slate hover:text-gobra-green"><span className="h-11 w-11 rounded-full bg-gobra-green/10 flex items-center justify-center"><Instagram className="h-5 w-5 text-gobra-green" /></span>{companyInfo.instagramHandle}</a>
            <div className="flex items-center gap-3 text-gobra-slate"><span className="h-11 w-11 rounded-full bg-gobra-green/10 flex items-center justify-center"><MapPin className="h-5 w-5 text-gobra-green" /></span>{companyInfo.address}</div>
          </div>
        </div>
        <div className="bg-white border border-gobra-sand rounded-2xl p-6 md:p-8">
          {sent ? (
            <div className="text-center py-10">
              <h3 className="font-title text-2xl font-bold text-gobra-green">{t('thanks_title')}</h3>
              <p className="text-muted-foreground mt-3">{t('thanks_msg')}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h3 className="font-title text-xl font-semibold text-gobra-slate">{t('enquire_now')}</h3>
              <Input required placeholder={t('name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <PhoneField value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })}
                testid="contact-mobile" placeholder={t('mobile')} />
              <Input required type="email" placeholder={t('email')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Textarea placeholder={t('note')} rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              {error && <p data-testid="contact-error" className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full bg-gobra-terracotta hover:bg-gobra-green text-white rounded-full py-6 text-base">{t('send_enquiry')}</Button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default Contact;
